// "Select game" (legacy window/games.js): 8 entries, stars, 150-char description with "more",
// an ad slot and Play. Play saves the choice and switches the game; closing discards an
// unconfirmed selection (Q-GAMES-1 fixed); "more" opens the rules of the clicked entry (Q-GAMES-2).
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { env } from '../env';
import { GAME_IDS, type GameId } from '../engine/types';
import { RULES } from '../engine/rules';
import { t } from '../i18n';
import { gamePreviewSrc, gamePreviewSrcSet } from '../options/assets';
import { useEngineState } from '../store/gameStore';
import { type WindowInstance } from '../store/uiStore';
import { AdSlot } from './AdSlot';
import { openRules, rulesUrl } from '../rules/rulesUrl';
import { adsEnabled, WindowFrame } from './WindowFrame';
import { MAHJONG_LAYOUTS, type Layout } from '../engine/rules/mahjong';
import { mahjongLayoutsFor } from '../board/geometry';
import { pickedMahjongLayout, pickMahjongLayout } from '../options/mahjongLayout';
import { pick358Length, picked358Length } from '../options/length358';
import { LENGTHS } from '../358/rules';
import { pick1000Target, picked1000Target } from '../options/target1000';
import { TARGETS } from '../1000/rules';
import { pickKulkiLine, pickedKulkiLine } from '../options/lineKulki';
import { LINES } from '../kulki/rules';
import { pickedSpiderColour, pickSpiderColour, SPIDER_COLOURS, type SpiderColour } from '../options/spiderColour';
import { SuitIcon } from '../hearts/suits';

const DIFFICULTY: Record<GameId, number> = {
  'klondike:turn-one': 2,
  'klondike:turn-three': 4,
  'double-klondike:turn-one': 3,
  'double-klondike:turn-three': 4,
  'spider:one-suit': 1,
  'spider:two-suits': 3,
  'spider:four-suits': 5,
  'forty-thieves': 5,
  freecell: 3,
  pyramid: 2,
  mahjong: 2,
  'mahjong-connect': 2,
  hearts: 3,
  makao: 2,
  'three-five-eight': 3,
  thousand: 4,
  kulki: 2,
};

/** A gap kept between the Mahjong shape list and an ad (the ADVERTISEMENT tab sits inside the ad's box, ads.css). */
const AD_TAB_PX = 8;
/** The least room for the shape list beside the ads (about three rows); with less it may cover an ad. */
const MIN_LIST_PX = 200;
/** How long a see-through cover stays after a shape was picked: a late click of that tap lands on it, not on what
 *  lay under the list (an ad). */
const SHIELD_MS = 800;
/** Games marked "New" in the list (owner 2026-10-23: FreeCell came with a "Nowość" tag); drop an id once the
 *  game is no longer new. */
const NEW_GAMES: ReadonlySet<GameId> = new Set<GameId>(['freecell', 'pyramid', 'mahjong', 'mahjong-connect', 'hearts', 'makao', 'three-five-eight', 'thousand', 'kulki']);

const stripTags = (html: string) => html.replace(/<[^>]*>/g, '');

/** The list's order (owner 2026-10-23, ready for more games): by kind (the families, today's order), by
 *  popularity (wins of the last 90 days, counted by the server once a week: stats/game-popularity.json) or by
 *  difficulty (the stars, easiest first). The pick is remembered in this browser. */
type SortMode = 'kind' | 'popular' | 'difficulty';
const SORT_MODES: SortMode[] = ['kind', 'popular', 'difficulty'];
const SORT_KEY = 'solitaire_storage:games:sort';

function readSort(): SortMode {
  try {
    const v = localStorage.getItem(SORT_KEY);
    return SORT_MODES.includes(v as SortMode) ? (v as SortMode) : 'kind';
  } catch {
    return 'kind';
  }
}

function saveSort(mode: SortMode) {
  try {
    localStorage.setItem(SORT_KEY, mode);
  } catch {
    /* no storage */
  }
}

/** Most popular first; null when the count cannot be read (the list keeps the kind order). Fetched once a page. */
let popularity: Promise<GameId[] | null> | null = null;
function loadPopularity(): Promise<GameId[] | null> {
  popularity ??= fetch(`${env.fullAddr}stats/game-popularity.json`)
    .then((r) => (r.ok ? (r.json() as Promise<{ games?: { game_type: string }[] }>) : null))
    .then((v) => (v?.games ? v.games.map((g) => g.game_type).filter((id): id is GameId => (GAME_IDS as readonly string[]).includes(id)) : null))
    .catch(() => null);
  return popularity;
}

/** Three groups, each sorted on its own: the solitaires, then the other card games (Kierki: a table of players, not a
 *  solitaire) under their own heading in every order (owner 2026-10-26), then the logic games (Kulki) at the very bottom
 *  under theirs (owner 2026-11-03). */
const SOLITAIRES = GAME_IDS.filter((id) => !RULES[id].table);
/** The other card games by kind (owner 2026-10-28): the trick-taking games together, Tysiąc beside Kierki, then Makao
 *  (the one where you get rid of your cards); a game missing here goes last. */
const OTHER_KIND: readonly GameId[] = ['hearts', 'thousand', 'three-five-eight', 'makao'];
const kindRank = (id: GameId) => (OTHER_KIND.includes(id) ? OTHER_KIND.indexOf(id) : OTHER_KIND.length);
const OTHER_GAMES = GAME_IDS.filter((id) => RULES[id].table && !RULES[id].logic).sort((a, b) => kindRank(a) - kindRank(b));
const LOGIC_GAMES = GAME_IDS.filter((id) => RULES[id].logic);
function sortedGames(group: readonly GameId[], mode: SortMode, popular: GameId[] | null): GameId[] {
  if (mode === 'difficulty') return [...group].sort((a, b) => DIFFICULTY[a] - DIFFICULTY[b]);
  if (mode === 'popular' && popular) {
    const known = popular.filter((id) => group.includes(id));
    return [...known, ...group.filter((id) => !known.includes(id))]; // a new game: last
  }
  return [...group];
}

/** Every game in the order this browser's Games window shows them; the online lobby's rows follow it (owner 2026-10-31:
 *  "posortuj te gry tak samo jak jest w wybierz grę"). */
export function useGameOrder(): GameId[] {
  const [sort] = useState<SortMode>(readSort);
  const [popular, setPopular] = useState<GameId[] | null>(null);
  useEffect(() => {
    if (sort !== 'popular') return;
    let live = true;
    void loadPopularity().then((list) => {
      if (live) setPopular(list);
    });
    return () => {
      live = false;
    };
  }, [sort]);
  return [...sortedGames(SOLITAIRES, sort, popular), ...sortedGames(OTHER_GAMES, sort, popular), ...sortedGames(LOGIC_GAMES, sort, popular)];
}

/** A shape's outline from its places (no picture to load): each tile a rounded box, the upper layers up and left and a
 *  shade lighter, as on the board. */
function ShapeOutline({ layout }: { layout: Layout }) {
  const fy = 152 / 112; // a tile's height to its width (board/geometry.ts TILE_FACE_H / TILE_FACE_W)
  const lift = (2 * 20) / 112; // a layer's step, in half tiles (TILE_LAYER_RATIO)
  const pad = layout.layers * lift + 0.6;
  return (
    <svg className="mj_shape_outline" viewBox={`${-pad} ${-pad} ${layout.w + pad + 0.6} ${layout.h * fy + pad + 0.6}`} aria-hidden="true">
      {layout.places.map((p, i) => (
        <rect key={i} x={p.x - p.z * lift} y={p.y * fy - p.z * lift} width={2} height={2 * fy} rx={0.25} fill={['#e9dfc6', '#f1e9d4', '#f7f1e2', '#fbf8ef', '#fffdf7'][p.z]} stroke="#7a6b4f" strokeWidth={0.18} />
      ))}
    </svg>
  );
}

/** "Random" in the shape list: a die (five pips) in the outlines' colours. */
function Dice() {
  return (
    <svg className="mj_shape_outline mj_dice" viewBox="0 0 24 24" aria-hidden="true">
      <rect x="3" y="3" width="18" height="18" rx="4" fill="#fffdf7" stroke="#7a6b4f" strokeWidth="1.3" />
      {[[8, 8], [16, 8], [12, 12], [8, 16], [16, 16]].map(([x, y]) => (
        <circle key={`${x}${y}`} cx={x} cy={y} r="1.6" fill="#7a6b4f" />
      ))}
    </svg>
  );
}

/** A game with a choice before it starts: Mahjong's shape, 3-5-8's length, Tysiąc's target, Spider one suit's colour
 *  (owner 2026-10-28: the same list). */
type ListFor = 'mahjong' | '358' | '1000' | 'kulki' | 'spider';

export function GamesWindow({ instance, onPlay }: { instance: WindowInstance; onPlay: (game: GameId, layout?: number | null, shapeBefore?: number | null, colourBefore?: SpiderColour) => void }) {
  const current = useEngineState().game; // the running game (a /#<game> start is not saved)
  const [selected, setSelected] = useState<GameId>(current);
  // Mahjong's shape (owner 2026-10-27): null a random one a deal, else the picked layout; the Mahjong row keeps it (owner
  // 2026-10-28: back to a game with a list, its saved value is dealt; Random is picked in the list)
  const [shape, setShape] = useState<number | null>(pickedMahjongLayout);
  // a pick is saved at once, like the other settings (owner 2026-10-28: after a reload the list showed the default);
  // Play compares the running deal with the shape saved when the window opened
  const [shapeBefore] = useState<number | null>(pickedMahjongLayout);
  // the shape list floats over the page (a portal, fixed): inside the scrolling list it made the list longer and the
  // page jump on an iPad (owner 2026-10-27); where it stands is measured from the pill when it opens
  const [shapesAt, setShapesAt] = useState<{ for: ListFor; left: number; width: number; top?: number; bottom?: number; maxH: number; pill: { x: number; y: number } } | null>(null);
  const pillRefs = { mahjong: useRef<HTMLButtonElement>(null), '358': useRef<HTMLButtonElement>(null), '1000': useRef<HTMLButtonElement>(null), kulki: useRef<HTMLButtonElement>(null), spider: useRef<HTMLButtonElement>(null) };
  // 3-5-8's length and Tysiąc's target (owner 2026-10-28: chosen before the game, in a list like Mahjong's shapes)
  const [length, setLength] = useState<number>(picked358Length);
  const [target, setTarget] = useState<number>(picked1000Target);
  // Kulki's version (owner 2026-11-03): 5 in a row, or the easier 4
  const [line, setLine] = useState<number>(pickedKulkiLine);
  // Spider one suit's colour (player report #uh0t4g74gj, owner 2026-10-28): saved at once; Play compares the running
  // deal with the colour saved when the window opened, as Mahjong's shape
  const [colour, setColour] = useState<SpiderColour>(pickedSpiderColour);
  const [colourBefore] = useState<SpiderColour>(pickedSpiderColour);
  const [shield, setShield] = useState(false);
  useEffect(() => {
    if (!shield) return;
    const id = setTimeout(() => setShield(false), SHIELD_MS);
    return () => clearTimeout(id);
  }, [shield]);
  const openShapes = (which: ListFor) => {
    const r = pillRefs[which].current?.getBoundingClientRect();
    if (!r) return;
    const vh = window.visualViewport?.height ?? innerHeight;
    const width = Math.min(310, innerWidth - 16);
    const left = Math.max(8, Math.min(r.left, innerWidth - width - 8));
    // never over an ad (owner 2026-10-27, iPad: a tap on a shape over the window's ad opened the ad: the list closed
    // on the finger's lift and Safari's own click came after it, onto the ad's frame, which no page script can stop;
    // a click on an ad by mistake can get the ad account blocked): the room ends a little before the first ad or at the
    // bottom ad band
    const ads = [...document.querySelectorAll('.ads:not([data-ad-hidden]), iframe')]
      .map((el) => el.getBoundingClientRect())
      .filter((a) => a.width > 0 && a.height > 0 && a.right > left && a.left < left + width);
    const band = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--ad-anchor-h')) || 0;
    const floor = Math.min(vh - band, ...ads.filter((a) => a.top >= r.bottom).map((a) => a.top - AD_TAB_PX));
    const ceiling = Math.max(0, ...ads.filter((a) => a.bottom <= r.top).map((a) => a.bottom));
    let below = floor - r.bottom - 12;
    let above = r.top - ceiling - 12;
    // a low screen with no room for a few rows beside the ads (owner 2026-10-27: avoid the ad where it can be done): the
    // whole screen; the tap fix (ui/tapClick.ts) and the cover kept after a pick (`shield`) keep a click off the ad
    if (Math.max(below, above) < MIN_LIST_PX) {
      below = vh - r.bottom - 12;
      above = r.top - 12;
    }
    // below the pill when the whole list fits there or there is more room than above, else above it
    const pill = { x: r.left, y: r.top };
    setShapesAt(below >= 360 || below >= above ? { for: which, left, width, top: r.bottom + 4, maxH: below, pill } : { for: which, left, width, bottom: vh - r.top + 4, maxH: above, pill });
  };
  useEffect(() => {
    if (!shapesAt) return;
    const close = () => setShapesAt(null);
    const key = (e: KeyboardEvent) => e.key === 'Escape' && (e.stopPropagation(), close());
    // the list scrolled or the screen changed and the pill moved away from the list (a scroll that leaves it in place, as
    // the window's own scroll to the chosen game just after it opens, keeps the list open)
    const scrolled = (e: Event) => {
      if (e.target instanceof Node && document.querySelector('.mj_shapes')?.contains(e.target)) return;
      const r = pillRefs[shapesAt.for].current?.getBoundingClientRect();
      if (!r || Math.abs(r.left - shapesAt.pill.x) > 2 || Math.abs(r.top - shapesAt.pill.y) > 2) close();
    };
    addEventListener('scroll', scrolled, true);
    addEventListener('resize', close);
    addEventListener('keydown', key, true);
    return () => {
      removeEventListener('scroll', scrolled, true);
      removeEventListener('resize', close);
      removeEventListener('keydown', key, true);
    };
  }, [shapesAt]); // eslint-disable-line react-hooks/exhaustive-deps -- the refs stay the same
  const [sort, setSort] = useState<SortMode>(readSort);
  const [popular, setPopular] = useState<GameId[] | null>(null);
  useEffect(() => {
    if (sort !== 'popular' || popular) return;
    let live = true;
    void loadPopularity().then((list) => {
      if (live) setPopular(list);
    });
    return () => {
      live = false;
    };
  }, [sort, popular]);
  // Opening the window brings the selected game into view (owner 2026-10-17: Forty Thieves, the last
  // entry, was hidden below the fold). Only the list scrolls; centred where the list has room.
  const listRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const entry = listRef.current?.querySelector<HTMLElement>('.option_item_entry.selected');
    if (!entry) return;
    let scroller: HTMLElement | null = entry.parentElement;
    while (scroller && !(scroller.scrollHeight > scroller.clientHeight && /auto|scroll/.test(getComputedStyle(scroller).overflowY))) scroller = scroller.parentElement;
    if (!scroller) return;
    const e = entry.getBoundingClientRect();
    const sc = scroller.getBoundingClientRect();
    scroller.scrollTop += e.top - sc.top - Math.max(0, (sc.height - e.height) / 2);
    // only on open: a click on another entry must not move the list under the finger
  }, []);
  const entry = (id: GameId) => {
    if (id === 'mahjong') return mahjongEntry(id); // Connect has no shapes to pick: a plain row
    if (id === 'spider:one-suit') return colourEntry(id);
    const table = RULES[id].table;
    return table === '358' || table === '1000' || table === 'kulki' ? choiceEntry(id, table) : row(id);
  };
  const row = (id: GameId) => (
    <a
      key={id}
      href="#"
      className={`option_item_entry${selected === id ? ' selected' : ''}`}
      data-item={`game|${id}`}
      onClick={(e) => {
        e.preventDefault();
        setSelected(id);
      }}
    >
      <img className="game_preview" src={gamePreviewSrc(id)} srcSet={gamePreviewSrcSet(id)} alt="" />
      <span className="stars">
        {Array.from({ length: 5 }, (_, i) => (
          <img key={i} src={`${env.ss}images/star${DIFFICULTY[id] > i ? '' : '_gray'}.png`} alt="" />
        ))}
        <span className="caption">{t('difficulty-level')}</span>
      </span>
      <span className="game_name">
        {t(id)}
        {NEW_GAMES.has(id) ? <span className="game_new">{t('games.new')}</span> : null}
      </span>
      <p className="game_desc">
        {stripTags(t(`rules.content.${id}`)).slice(0, 150)} ...{' '}
        <a
          href={rulesUrl(id)}
          target="_blank"
          rel="noopener"
          className="more"
          onClick={(e) => {
            // the row itself is a link that selects the game: open the rules page only
            e.preventDefault();
            e.stopPropagation();
            openRules(id);
          }}
        >
          {t('game.more')}
        </a>
      </p>
    </a>
  );
  // Mahjong (owner 2026-10-27, variant C of prototype/mj-shape-picker): a small "Shape: Random" button in its row opens
  // a list of the shapes over the window; random is the default; a shape that does not fit this screen says so (its
  // tiles would be small)
  const board = document.getElementById('main');
  const fits = new Set(mahjongLayoutsFor(board?.clientWidth || innerWidth, board?.clientHeight || innerHeight * 0.8));
  const choices: Array<number | null> = [null, ...MAHJONG_LAYOUTS.map((_, i) => i)];
  const shapeName = (i: number | null) => t(i === null ? 'mahjong.layout.randomShort' : `mahjong.layout.${MAHJONG_LAYOUTS[i]!.name}`);
  const mahjongEntry = (id: GameId) => {
    const current = shape;
    return (
      <div key={id} className={`mj_entry${selected === id ? ' selected' : ''}`}>
        {row(id)}
        <button
          type="button"
          ref={pillRefs.mahjong}
          className="mj_shape_pill"
          data-pill="mahjong"
          aria-haspopup="listbox"
          aria-expanded={shapesAt?.for === 'mahjong'}
          onClick={() => (shapesAt ? setShapesAt(null) : openShapes('mahjong'))}
        >
          {t('mahjong.layout.label')}: <strong>{shapeName(current)}</strong>
        </button>
        {shapesAt?.for === 'mahjong'
          ? createPortal(
              // a plain wrapper: a fixed layer straight under <body> is taken for the anchor ad (app/AdAnchorBand.tsx
              // looks at the body's fixed children reaching the screen's bottom) and the windows shrank to nothing
              <div className="mj_shapes_layer">
                <div className="mj_shapes_catch" onClick={() => setShapesAt(null)} />
                <div
                  className="mj_shapes"
                  role="listbox"
                  aria-label={t('mahjong.layout.label')}
                  style={{ left: shapesAt.left, width: shapesAt.width, top: shapesAt.top, bottom: shapesAt.bottom, maxHeight: shapesAt.maxH }}
                >
                  {choices.map((i) => (
                    <button
                      key={String(i)}
                      type="button"
                      role="option"
                      aria-selected={current === i}
                      className={`mj_shape${current === i ? ' selected' : ''}`}
                      data-layout={i === null ? 'random' : MAHJONG_LAYOUTS[i]!.name}
                      onClick={() => {
                        setSelected(id);
                        setShape(i);
                        pickMahjongLayout(i);
                        setShapesAt(null);
                        setShield(true);
                      }}
                    >
                      {i === null ? <Dice /> : <ShapeOutline layout={MAHJONG_LAYOUTS[i]!} />}
                      <span className="mj_shape_name">{i === null ? t('mahjong.layout.random') : shapeName(i)}</span>
                      {i !== null && !fits.has(i) ? <span className="mj_shape_note">{t('mahjong.layout.small')}</span> : null}
                    </button>
                  ))}
                </div>
              </div>,
              document.body,
            )
          : shield
            ? shieldLayer()
            : null}
      </div>
    );
  };
  const shieldLayer = () =>
    createPortal(
      <div className="mj_shapes_layer">
        <div className="mj_shapes_catch" data-shield="" />
      </div>,
      document.body,
    );
  // 3-5-8 and Tysiąc (owner 2026-10-28): "Długość: 6 rozdań" / "Gra do: 1000" in the row opens its values in the same
  // list as Mahjong's shapes; a pick is saved at once
  const CHOICES = {
    '358': { values: LENGTHS as readonly number[], value: length, set: setLength, save: pick358Length, key: 't58.length' },
    '1000': { values: TARGETS as readonly number[], value: target, set: setTarget, save: pick1000Target, key: 't1k.target' },
    kulki: { values: LINES as readonly number[], value: line, set: setLine, save: pickKulkiLine, key: 'kulki.line' },
  } as const;
  const choiceEntry = (id: GameId, which: '358' | '1000' | 'kulki') => {
    const c = CHOICES[which];
    const name = (n: number) => t(`${c.key}.${n}`);
    return (
      <div key={id} className={`mj_entry${selected === id ? ' selected' : ''}`}>
        {row(id)}
        <button
          type="button"
          ref={pillRefs[which]}
          className="mj_shape_pill"
          data-pill={which}
          aria-haspopup="listbox"
          aria-expanded={shapesAt?.for === which}
          onClick={() => (shapesAt ? setShapesAt(null) : openShapes(which))}
        >
          {t(`${c.key}.label`)}: <strong>{name(c.value)}</strong>
        </button>
        {shapesAt?.for === which
          ? createPortal(
              <div className="mj_shapes_layer">
                <div className="mj_shapes_catch" onClick={() => setShapesAt(null)} />
                <div
                  className="mj_shapes"
                  role="listbox"
                  aria-label={t(`${c.key}.label`)}
                  style={{ left: shapesAt.left, width: shapesAt.width, top: shapesAt.top, bottom: shapesAt.bottom, maxHeight: shapesAt.maxH }}
                >
                  {c.values.map((n) => (
                    <button
                      key={n}
                      type="button"
                      role="option"
                      aria-selected={c.value === n}
                      className={`mj_shape${c.value === n ? ' selected' : ''}`}
                      data-length={which === '358' ? n : undefined}
                      data-target={which === '1000' ? n : undefined}
                      data-line={which === 'kulki' ? n : undefined}
                      onClick={() => {
                        setSelected(id);
                        c.set(n);
                        c.save(n);
                        setShapesAt(null);
                        setShield(true);
                      }}
                    >
                      <span className="mj_length_num" data-wide={n >= 100 || undefined} aria-hidden="true">
                        {n}
                      </span>
                      <span className="mj_shape_name">{name(n)}</span>
                      <span className="mj_shape_note">{t(`${c.key}.note.${n}`)}</span>
                    </button>
                  ))}
                </div>
              </div>,
              document.body,
            )
          : shield
            ? shieldLayer()
            : null}
      </div>
    );
  };
  // Spider one suit (player report #uh0t4g74gj, owner 2026-10-28): "Colour: Random" in the row opens Random / Black /
  // Red in the same list; a pick is saved at once
  const colourName = (c: SpiderColour) => t(`spider.colour.${c === null ? 'random' : c === 's' ? 'black' : 'red'}`);
  const colourEntry = (id: GameId) => (
    <div key={id} className={`mj_entry${selected === id ? ' selected' : ''}`}>
      {row(id)}
      <button
        type="button"
        ref={pillRefs.spider}
        className="mj_shape_pill"
        data-pill="spider"
        aria-haspopup="listbox"
        aria-expanded={shapesAt?.for === 'spider'}
        onClick={() => (shapesAt ? setShapesAt(null) : openShapes('spider'))}
      >
        {t('spider.colour.label')}: <strong>{colourName(colour)}</strong>
      </button>
      {shapesAt?.for === 'spider'
        ? createPortal(
            <div className="mj_shapes_layer">
              <div className="mj_shapes_catch" onClick={() => setShapesAt(null)} />
              <div
                className="mj_shapes"
                role="listbox"
                aria-label={t('spider.colour.label')}
                style={{ left: shapesAt.left, width: shapesAt.width, top: shapesAt.top, bottom: shapesAt.bottom, maxHeight: shapesAt.maxH }}
              >
                {SPIDER_COLOURS.map((c) => (
                  <button
                    key={String(c)}
                    type="button"
                    role="option"
                    aria-selected={colour === c}
                    className={`mj_shape${colour === c ? ' selected' : ''}`}
                    data-colour={c ?? 'random'}
                    onClick={() => {
                      setSelected(id);
                      setColour(c);
                      pickSpiderColour(c);
                      setShapesAt(null);
                      setShield(true);
                    }}
                  >
                    {c === null ? <Dice /> : <span className="mj_colour_suit" aria-hidden="true"><SuitIcon s={c === 's' ? 'S' : 'H'} /></span>}
                    <span className="mj_shape_name">{c === null ? t('spider.colour.randomLong') : colourName(c)}</span>
                  </button>
                ))}
              </div>
            </div>,
            document.body,
          )
        : shield
          ? shieldLayer()
          : null}
    </div>
  );
  return (
    <WindowFrame
      name="games"
      header={t('optionWindowTabGameHeader')}
      overlay
      closing={instance.closing}
      className={adsEnabled() ? '' : 'no-ads'}
    >
      <div className="games_wrapper" ref={listRef}>
        <div className="games_sort" role="group" aria-label={t('games.sort.label')}>
          <span className="games_sort_label">{t('games.sort.label')}</span>
          {SORT_MODES.map((mode) => (
            <button
              key={mode}
              type="button"
              className={`games_sort_btn${sort === mode ? ' on' : ''}`}
              data-sort={mode}
              aria-pressed={sort === mode}
              onClick={() => {
                setSort(mode);
                saveSort(mode);
              }}
            >
              {t(`games.sort.${mode}`)}
            </button>
          ))}
        </div>
        <div className="games_window">
          <div className="option_item radio">
            {sortedGames(SOLITAIRES, sort, popular).map(entry)}
            {OTHER_GAMES.length ? (
              <div className="games_group" role="separator">
                <span>{t('games.otherCardGames')}</span>
              </div>
            ) : null}
            {sortedGames(OTHER_GAMES, sort, popular).map(entry)}
            {LOGIC_GAMES.length ? (
              <div className="games_group" role="separator">
                <span>{t('games.otherLogicGames')}</span>
              </div>
            ) : null}
            {sortedGames(LOGIC_GAMES, sort, popular).map(entry)}
          </div>
        </div>
        <AdSlot slot={1} />
        <div className="game_button_cont">
          <input type="button" value={t('game.play')} onClick={() => onPlay(selected, selected === 'mahjong' ? shape : RULES[selected].table === '358' ? length : RULES[selected].table === '1000' ? target : RULES[selected].table === 'kulki' ? line : undefined, shapeBefore, selected === 'spider:one-suit' ? colourBefore : undefined)} />
        </div>
      </div>
    </WindowFrame>
  );
}
